import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Option } from "@/components/dropdown/Option";
import { ExpressionRealNodePreview } from "@/components/expression/preview/ExpressionNodePreview";
import { Icon } from "@/components/icons/Icon";
import { insertNodeFromSuggestion } from "@/components/suggestions/utils";
import { cursorIsActive } from "@/models/CursorModel";
import { createNodeFrom } from "@/models/nodes/createNodeFrom";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { keyMatcher } from "@/utils/keyMatcher";
import { twMerge } from "@/utils/twMerge";

import type { ViewerNode } from "@/components/doc/right-sidebar/node-viewer/types";

type Props = {
  node: ViewerNode;
  isActive?: boolean;
};

/** What an object row is read as, e.g. "x Variable": its toggle's name */
export function useNodeShortViewLabel(node: ViewerNode) {
  const t = useTranslations("sidebar.nodeProps.nodeType");
  const type = t(node.type);
  return `${"value" in node ? `${node.value} ` : ""}${type}`;
}

/**
 * An object row's content, shown over its toggle button: the text is hidden from screen readers,
 * which read the toggle's name instead, and the preview is a separate insert button.
 */
export const NodeShortView = observer(function NodeShortView({ node, isActive }: Props) {
  const t = useTranslations("sidebar.nodeProps");
  const {
    doc,
    doc: {
      editor: { editorState },
    },
  } = node;

  const { name } = node;
  const type = t(`nodeType.${node.type}`);
  const label = useNodeShortViewLabel(node);

  const handleNodeSelect = (selectedNode: ViewerNode) => {
    doc.transact(
      () => {
        const { editor } = doc;

        if (!editor.tiptap) {
          return;
        }

        const newNode = createNodeFrom(selectedNode, doc, { resetIds: true });
        if (!newNode) return;

        if (editorState.isExpression && cursorIsActive(doc.cursor)) {
          const { node: addedNode } = insertNodeFromSuggestion(doc.cursor, newNode);
          doc.cursor.moveAfterNode(addedNode);
        } else {
          const newInlineNode = doc.create(InlineNodeModel, [newNode]);
          editor.tiptap.chain().insertInlineExpression(newInlineNode).run();
          if (newNode.parent) {
            doc.cursor.moveTo(newNode.parent, newNode.parent.nodes.length);
          }
        }
      },
      "objectViewerNode:click",
      { forceNewUndoStack: true },
    );
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>, selectedNode: ViewerNode) => {
    e.preventDefault();
    handleNodeSelect(selectedNode);
  };

  // handle click before the wrapping Disclosure, which can prevent the event from propagating
  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, selectedNode: ViewerNode) => {
    if (keyMatcher("Enter", e) || keyMatcher("Space", e)) {
      e.preventDefault();
      e.stopPropagation();
      handleNodeSelect(selectedNode);
    }
  };

  return (
    <Option
      as="div"
      // Not a Tab stop of its own: the toggle under it is
      tabIndex={undefined}
      disableHoverStyles
      className={twMerge(
        "relative pointer-events-none group-hover:bg-blue-100 dark:group-hover:bg-gray-900",
        // The toggle under this row is focused, but the row's background would hide its ring
        "peer-focus-visible:outline-solid peer-focus-visible:outline-2 peer-focus-visible:-outline-offset-2 peer-focus-visible:outline-blue-500",
      )}
      icon={
        <div className="flex items-center">
          <Icon
            aria-hidden
            name="ChevronDown"
            className={twMerge("-mr-[2px] -ml-[8px] shrink-0", !isActive && "-rotate-90")}
          />
          {doc.editable ? (
            <button
              type="button"
              aria-label={t("insertNode", { label })}
              className={twMerge(
                "relative z-10 pointer-events-auto",
                // The preview covers an outline drawn on the button itself, so the ring is an overlay
                "focus-visible:outline-none focus-visible:after:content-[''] focus-visible:after:absolute focus-visible:after:inset-0 focus-visible:after:rounded-6 focus-visible:after:pointer-events-none",
                "focus-visible:after:outline-solid focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-blue-500",
              )}
              onClick={(e) => handleClick(e, node)}
              onKeyDown={(e) => handleKeyDown(e, node)}
            >
              <ExpressionRealNodePreview renderContext="objects-tree" node={node} doc={doc} />
            </button>
          ) : (
            <ExpressionRealNodePreview renderContext="objects-tree" node={node} doc={doc} />
          )}
        </div>
      }
      // Only when named: any title gets a "·" separator before it
      title={name ? <span aria-hidden>{name}</span> : undefined}
      subTitle={<span aria-hidden>{type}</span>}
    />
  );
});
